import {
  BulkInvitationColumnSpec,
  BulkInvitationFieldOption,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";

export const TEAM_MEMBER_ACCESS_TEMPLATE_KEY = "accessTemplateId";

/**
 * A firm's colleagues, listed by their team leader: everyone who will work and bill on the
 * client's matters.
 *
 * Built rather than declared, because one column's choices are loaded: the organization's
 * permission templates, which the API answers only to a leader with Member Permissions (#6826).
 * Without them the column is left out, rather than offered with nothing to choose: the API would
 * refuse any template that leader named.
 */
export default function createTeamMemberRosterColumns(
  accessTemplates: readonly BulkInvitationFieldOption[] = [],
  offersAccessTemplates = true,
): BulkInvitationColumnSpec {
  const spec: BulkInvitationColumnSpec = {
    id: "team-members",
    noun: "colleague",
    templateFileName: "attorney-hub-team-members",
    templateSheetName: "Team members",
    fields: [
      {
        key: "email",
        header: "Email address",
        kind: "email",
        required: true,
        hint: "Must be on a domain your firm accepts invitations to.",
        example: "jane.doe@example.invalid",
      },
      { key: "firstName", header: "First name", kind: "text", required: true, example: "Jane" },
      { key: "lastName", header: "Last name", kind: "text", required: true, example: "Doe" },
      {
        key: "title",
        header: "Title or responsibility",
        kind: "text",
        required: true,
        // Required here and optional on a single invitation, which is not an inconsistency: the
        // procedure this list serves depends on the client being able to find a billing
        // administrator in it without asking anybody.
        hint: "Name at least one billing administrator in the list, so the client knows who to ask about an invoice.",
        example: "Billing administrator",
      },
      {
        key: "requestedMarketplaceVisibility",
        header: "Publish profile to Marketplace",
        kind: "boolean",
        required: false,
        hint: "Applied when they accept, if their profile lists professional credentials by then. They own the profile from that moment and can change it.",
        example: "Yes",
      },
      {
        key: TEAM_MEMBER_ACCESS_TEMPLATE_KEY,
        header: "Permission template",
        kind: "select",
        required: false,
        options: accessTemplates,
        // Kept out of the spreadsheet: its value is a GUID, and somebody filling a sheet in knows
        // their colleague's title but not a template's id. In the grid it is a list of names.
        omitFromSpreadsheet: true,
        hint: "Applied when they accept. Leave blank for the basic member permissions.",
      },
    ],
    // 848px with the actions column, and 1,028px with the Problem column too, so it fits the
    // 1,046px pane beside the navigation at a 1366 window either way.
    gridColumns: [
      { id: "email", header: "Email address", keys: ["email"], size: 200, pinned: true },
      { id: "name", header: "Name", keys: ["firstName", "lastName"], size: 130, pinned: true },
      { id: "title", header: "Title or responsibility", keys: ["title"], size: 150 },
      {
        id: "requestedMarketplaceVisibility",
        header: "Publish profile to Marketplace",
        keys: ["requestedMarketplaceVisibility"],
        size: 120,
      },
      { id: TEAM_MEMBER_ACCESS_TEMPLATE_KEY, header: "Permission template", keys: [TEAM_MEMBER_ACCESS_TEMPLATE_KEY], size: 160 },
    ],
  };
  if (offersAccessTemplates) return spec;
  return {
    ...spec,
    fields: spec.fields.filter((field) => field.key !== TEAM_MEMBER_ACCESS_TEMPLATE_KEY),
    gridColumns: spec.gridColumns.filter((column) => column.id !== TEAM_MEMBER_ACCESS_TEMPLATE_KEY),
  };
}
